import React, {useMemo} from "react"
import {RouteComponentProps, withRouter, Redirect} from "react-router-dom"
import {walletTracking} from "@irati-tracking/wallet"
import authentication from "@irati/auth"
import {PaymentCurrencyType, User, WalletEntityType} from "@irati/types"
import history from "@irati/utils/history"
import {PartialRoute} from "@irati/utils/history/routing"
import Loading from "@irati/ui/src/Loading"

import {useOnce} from "@irati/utils/hooks/useOnce"
import {useWalletQuery, useWalletWithNextTopupQuery} from "@irati/wallet/hooks/wallet"

import SendCreditSuccessModal from "../../components/SendCreditSuccessModal"
import {CashTransactions, FlexiPointsTransactions, RewardPointsTransactions} from "../../components/Transactions"
import WalletBalanceCard from "../../components/WalletBalanceCard"
import Container from "../styled/Container"
import {Intro} from "./Intro"
import CustomRewardsTransactions from "../../components/Transactions/CustomRewardsTransactions"
import {useMyListCustomRewardsTransactionsQuery} from "@irati/custom-reward/hooks"

interface Props extends RouteComponentProps<{}, {}, {recipient?: User; amount?: number}> {}

const WalletContainer = ({location, match: {path}}: Props) => {
  const {data: customRewardTransactions, loading} = useMyListCustomRewardsTransactionsQuery(
    {},
    {
      skip: !authentication.apps("platform.customRewards")
    }
  )
  const showCustomReward = !!customRewardTransactions?.results?.length
  useOnce(() => {
    walletTracking.viewHome()
  })

  const {recipient, amount} = location.state || {}
  const userId = authentication.user().uuid!

  const {load: l1, data: wallet} = useWalletQuery(
    {userId, currency: PaymentCurrencyType.Gbp, entityType: WalletEntityType.User, entityId: userId},
    {fetchPolicy: "network-only", skip: !authentication.apps("wallet.users")}
  )
  const {load: l2, data: tokenWallet} = useWalletWithNextTopupQuery(
    {userId, currency: PaymentCurrencyType.Tt, entityType: WalletEntityType.User, entityId: userId},
    {fetchPolicy: "network-only", skip: !authentication.apps("wallet.treatTokens")}
  )
  const {load: l3, data: rewardWallet} = useWalletQuery(
    {userId, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.User, entityId: userId},
    {fetchPolicy: "network-only", skip: !authentication.apps("wallet.flexiRewards")}
  )

  const load = useMemo(() => [l1, l2, l3], [l1, l2, l3])

  return (
    <Container>
      <Loading load={load}>
        <Intro />

        {!loading && (
          <WalletBalanceCard wallet={wallet} tokenWallet={tokenWallet} rewardWallet={rewardWallet} hasCustomReward={showCustomReward} />
        )}

        {showCustomReward && (
          <PartialRoute match={path} path="/custom-rewards" component={CustomRewardsTransactions} componentProps={{isUser: true}} />
        )}
        <PartialRoute match={path} path="/flexi-points" component={FlexiPointsTransactions} componentProps={{wallet: tokenWallet}} />
        <PartialRoute match={path} path="/reward-points" component={RewardPointsTransactions} componentProps={{wallet: rewardWallet}} />
        <PartialRoute
          match={path}
          path="/cash"
          component={CashTransactions}
          componentProps={{
            wallet
          }}
        />
        <PartialRoute
          exact
          match={path}
          path="/"
          component={() => {
            if (!authentication.apps("wallet.treatTokens") && authentication.apps("wallet.flexiRewards")) {
              return <Redirect to="/wallet/reward-points" />
            } else if (authentication.apps("wallet.treatTokens")) {
              return <Redirect to="/wallet/flexi-points" />
            } else {
              return <Redirect to="/wallet/cash" />
            }
          }}
        />

        {!!recipient && (
          <SendCreditSuccessModal
            recipient={recipient}
            value={amount}
            onClose={() => {
              // replace current url without state which drives showing popup
              const currentUrl = location.pathname
              history.replace(currentUrl)
            }}
          />
        )}
      </Loading>
    </Container>
  )
}

export default withRouter(WalletContainer)
